import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';

import { PBBIncentiveBanner } from 'src/payments/components/pbb-incentive-banner/pbb-incentive-banner';
import { PaymentSelectionProvider, TabValue } from 'src/checkout/components/sections/payment/shared/context';
import { InstorePaymentOption } from './instore-options';
import { PaymentSummary } from '../shared/payment-summary';
import { PaymentTabs } from './payment-tabs';
import { OnlinePaymentOptions } from './online-options';
import { useSelections } from './use-selection';

type SelectionProps = { displayedInConfirmation?: boolean };

const Selection = ({ displayedInConfirmation }: SelectionProps): JSX.Element => {
  const {
    showPaymentBanner,
    tabOptions,
    onTabChange,
    tabValue,
    showFeeBadgeInline,
    showTabs,
    showOnlinePaymentOptionOnly,
    showInstorePaymentOptionOnly,
    showPaymentSummary,
    plaidEmbeddedSearchVisible,
    unenrolledAlertMessage,
  } = useSelections();

  return (
    <CheckoutLabelGrouping id='payment' data-testid='payment-section'>
      <CheckoutLabel>
        <StyledCheckoutSectionHeading displayedInConfirmation={displayedInConfirmation}>
          Select how you would like to pay:
        </StyledCheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        {showPaymentBanner && <PBBIncentiveBanner marginBottom='12px' />}
        {showTabs && (
          <PaymentTabs
            tabOptions={tabOptions}
            selectedTab={tabValue}
            onTabChange={onTabChange}
            showFeeBadgeInline={showFeeBadgeInline}
            showEmbeddedSearch={plaidEmbeddedSearchVisible}
            alertMessage={unenrolledAlertMessage}
          >
            {tabValue === TabValue.online && <OnlinePaymentOptions />}
            {tabValue === TabValue.offline && <InstorePaymentOption containerStyles={{ border: 'none' }} />}
          </PaymentTabs>
        )}

        {showInstorePaymentOptionOnly && <InstorePaymentOption />}
        {showOnlinePaymentOptionOnly && <OnlinePaymentOptions />}
        {showPaymentSummary && <PaymentSummary includeContainer />}
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
};

export const PaymentSelection = ({ displayedInConfirmation = false }: SelectionProps): JSX.Element => (
  <PaymentSelectionProvider>
    <ObserverSelection displayedInConfirmation={displayedInConfirmation} />
  </PaymentSelectionProvider>
);

const ObserverSelection = observer(Selection);

const StyledCheckoutSectionHeading = styled(CheckoutSectionHeading)<{
  displayedInConfirmation?: boolean;
}>`
  line-height: 24px;
  ${({ displayedInConfirmation }) =>
    displayedInConfirmation &&
    css`
      font-size: 16px !important;
    `}
`;
